<script setup>
import { ref, onMounted, computed } from 'vue';
import { Field, useForm } from 'vee-validate';
import {
    Listbox,
    ListboxButton,
    ListboxOptions,
    ListboxOption,
} from '@headlessui/vue';
import { __, SelfcareAccountSubscriberLineManagment } from '@/utils';
import Modal from "@components/modal.vue";
import Loader from '@components/loader.vue';
import Toggle from '@components/toggle/toggle.vue';

import SettingsTablesModalBusy from './settingsTables-modal-busy.vue';
import ModalError from './settings-modal-error.vue';

const props = defineProps({
  nc_data : {
    type: Object
  },
  subscriber: {
    type: Object
  }
});

const { setValues, values, setFieldValue } = useForm({
  initialValues: {
    subscriber_id: props.subscriber.id,
    nc_in_call_fwd_noanswer_c: false,
    nc_in_call_fwd_permanent_c: false,
    nc_out_call_intl_c: false,
    nc_out_call_144_c: false,
    nc_out_call_rashap_c: false,
    nc_roaming_c: false,
    nc_in_sms_c: false,
    nc_out_sms_intl_c: false,
    nc_out_call_intl_operator_c: '',
  },
  keepValuesOnUnmount: true,
});

const loader = ref(false);
const selectedTable = ref(0);
const isSuccess = ref(false);
const tempPhone = ref('');
const intlOperator = ref('אקספון')

const busyModal = ref(false);
const modalData = ref({
  modalText: '',
  modalResponseKey: '',
  modalPhone: ''
});
const showErrorModal = ref(false);

const tables = ref([
  {
    id: 0,
    title: __('Line settings'),
    data: [
      // { text: __('Call Forwarding If No Answer'), responseKey: 'nc_in_call_fwd_noanswer_c', isChecked: 0, hasPhone: 'nc_in_call_fwd_noanswer_num_c' },
      // { text: __('Fixed Call Forwarding'), responseKey: 'nc_in_call_fwd_permanent_c', isChecked: 0, hasPhone: 'nc_in_call_fwd_permanent_num_c' },
      { text: __('Calls to International Destinations'), responseKey: 'nc_out_call_intl_c', isChecked: 0 },
      { text: __('Calls to 144'), responseKey: 'nc_out_call_144_c', isChecked: 0 },
      { text: __('Calls to the Palestinian Authority'), responseKey: 'nc_out_call_rashap_c', isChecked: 0 },
      { text: __('Roaming Abroad'), responseKey: 'nc_roaming_c', isChecked: 0 },
      // { text: __('Receiving SMS Messages'), responseKey: 'nc_in_sms_c', isChecked: 0 },
      // { text: __('Sending SMS Messages Abroad'), responseKey: 'nc_out_sms_intl_c', isChecked: 0 },
      { 
        text: __('International Operator'), 
        responseKey: 'nc_out_call_intl_operator_c', 
        isChecked: '', 
        isSelect: [
          {value: 'CS012', label: 'קווי זהב/012'},
          {value: 'CS013', label: 'ברק/נטוויזין'},
          {value: 'CS014', label: 'בזק בינלאומי'},
          {value: 'CS015', label: 'הלו'},
          {value: 'CS016', label: 'גולן'},
          {value: 'CS017', label: 'הוט'},
          {value: 'CS018', label: 'אקספון'},
          {value: 'CS019', label: 'טלזר'},
        ] 
      },
    ],
  },
  // {
  //   id: 1,
  //   title: __('Browsing and content'),
  //   data: [
  //     { text: __('Browsing in the country'), isChecked: 0, responseKey: 'nc_out_call_1900_c' },
  //     { text: __('Roaming abroad'), isChecked: 0, responseKey: 'nc_roaming_c' },
  //   ],
  // },
]);

const updateTablesFromData = (data) => {
  const dataArray = Object.entries(data);
  const dataObject = dataArray.reduce((acc, [key, value]) => {
    if(value === "1" || value === 1) {
      acc[key] = true
    } else if(value === "0" || value === 0 || value === '') {
      acc[key] = false
    } else {
      acc[key] = value
    }
    return acc;
  }, {});

  setValues({
    nc_in_call_fwd_noanswer_c: dataObject.nc_in_call_fwd_noanswer_c || false,
    nc_in_call_fwd_noanswer_num_c: dataObject.nc_in_call_fwd_noanswer_num_c || '',
    nc_in_call_fwd_permanent_c: dataObject.nc_in_call_fwd_permanent_c || false,
    nc_in_call_fwd_permanent_num_c: dataObject.nc_in_call_fwd_permanent_num_c || '',
    nc_out_call_intl_c: dataObject.nc_out_call_intl_c || false,
    nc_out_call_144_c: dataObject.nc_out_call_144_c || false,
    nc_out_call_rashap_c: dataObject.nc_out_call_rashap_c || false,
    nc_roaming_c: dataObject.nc_roaming_c || false,
    nc_in_sms_c: dataObject.nc_in_sms_c || false,
    nc_out_sms_intl_c: dataObject.nc_out_sms_intl_c || false,
    nc_out_call_intl_operator_c: dataObject.nc_out_call_intl_operator_c || '',
  })

  intlOperator.value = dataObject.nc_out_call_intl_operator_c;
};

const setModal = (key, checked) => {
  const hasKey = key === 'nc_in_call_fwd_noanswer_c' || key === 'nc_in_call_fwd_permanent_c';
  const valueIsFalse = checked === false || checked === 0;

  if(!hasKey || !valueIsFalse) {
    return false;
  }

  if(key === 'nc_in_call_fwd_noanswer_c') {
    modalData.value = {
      modalText: __('To forward calls to another number if busy, enter the phone number.'),
      modalResponseKey: 'nc_in_call_fwd_noanswer_c',
      modalPhone: 'nc_in_call_fwd_noanswer_num_c'
    };
  }

  if(key === 'nc_in_call_fwd_permanent_c') {
    modalData.value = {
      modalText: __('To forward calls to another number if no reception or the device is turned off, enter the phone number.'),
      modalResponseKey: 'nc_in_call_fwd_permanent_c',
      modalPhone: 'nc_in_call_fwd_permanent_num_c'
    };
  }
  
  if(hasKey) {
    busyModal.value = true;
    return true;
  }
}

const toggleChecked = async (name, value) => {
  const openModal = setModal(name, !value);

  if(openModal) {
    return;
  }
  
  setFieldValue(name, value);
  makeUpdateRequest({
    [name]: value,
  });
};

const currentTabData = computed(() => tables.value[selectedTable.value]?.data);

const updatePhone = (modalValue) => {
  tempPhone.value = values[modalData.value.modalPhone];
  setFieldValue(modalData.value.modalPhone, modalValue['phone']);
  makeUpdateRequest({
    [modalData.value.modalResponseKey]: true,
    [modalData.value.modalPhone]: modalValue['phone'],
  });
}

const editPhone = (itemIndex) => {
  const item = currentTabData.value[itemIndex];
  setModal(item.responseKey, false)
}

const makeUpdateRequest = async (value) => {
  const payload = {
    "subscriber_id": props.subscriber.id,
    ...value
  }
  try {
    await SelfcareAccountSubscriberLineManagment(payload);
    if(busyModal.value) {
      busyModal.value = false;
      isSuccess.value = true;
      setFieldValue(modalData.value.modalResponseKey, true);
    }
  } catch (error) {
    if(busyModal.value) {
      setFieldValue(modalData.value.modalPhone, tempPhone.value);
    }
    showErrorModal.value = true;  
  }
}

const closeModal = () => {
  busyModal.value = false;
  isSuccess.value = false;
}

const selectedIntl = computed(() => {
  const selected = tables.value[selectedTable.value].data.find(item => item.responseKey === 'nc_out_call_intl_operator_c');
  return selected?.isSelect.find(item => item.value === intlOperator.value);
});

onMounted(() => {
  if (props.nc_data) {
    updateTablesFromData(props.nc_data);
  }
});
</script>
<template>
  <Loader v-if="loader"  color="#2563eb" />
  <div class="account-main--settings_heading">
      <label v-for="(table, index) in tables" :key="table.id" class="mr-4" :class="{ 'gradient': selectedTable === table.id }">
        <input
          type="radio"
          :value="index"
          v-model="selectedTable"
        />
        {{ table.title }}
      </label>
  </div>
  <form>
    <Field type="hidden" name="subscriber_id" :value="subscriber.id" />
    <Field type="hidden" name="nc_in_call_fwd_noanswer_num_c" />
    <Field type="hidden" name="nc_in_call_fwd_permanent_num_c" />
    <div v-if="tables.length && !loader">
      <div class="settings-grid">
        <div class="settings-grid__row" v-for="(item, index) in tables[selectedTable].data" :key="item.text">
          <div class="settings-grid__item">
            <span class="settings-grid__phone" v-if="item.hasPhone && values[item.hasPhone]">
              {{ values[item.hasPhone] }}
              <button type="button" class="link-icon" @click="editPhone(index)">
                <img :src="`${theme_path.url}/resources/images/svg/icon-edit2.svg`" />
              </button>
            </span>
            {{ item.text }}
          </div>
          <div class="settings-grid__item">
            <Toggle
              :name="item.responseKey"
              :value="values[item.responseKey]"
              @toggle="toggleChecked(item.responseKey, $event)"
              v-if="!item.isSelect"
            />
            <div class="range-select range-select--left" v-if="item.isSelect">
              <Listbox v-model="intlOperator" @update:model-value="toggleChecked(item.responseKey, $event)">
                  <ListboxButton class="range-select__selected minw250 ta-init">{{ selectedIntl?.label }}</ListboxButton>
                  <ListboxOptions class="range-select__dropdown">
                    <ListboxOption class="range-select__item" v-for="(item) in item.isSelect" :key="item.value" :value="item.value">
                      <div class="range-select__item__content">
                        {{ item.label }}
                      </div>
                    </ListboxOption>
                  </ListboxOptions>
              </Listbox>
            </div>
          </div>
        </div>
      </div>
    </div>
  </form>
  
  <SettingsTablesModalBusy 
    :isActive="busyModal"
    @close="closeModal"
    @submit="updatePhone($event)"
    :selectedModalData="modalData"
  />

  <Modal :isActive="isSuccess" @close="closeModal" class="popup--small">
    <template #body>
        <img :src="`${theme_path.url}/resources/images/svg/success-check.svg`" alt="Success">
        <div class="mb24">
          <h4 class="fz26">
            <strong>{{ __('The referral has been made!') }}</strong>
          </h4>
        </div>
        <p v-if="modalData.modalPhone === 'nc_in_call_fwd_noanswer_num_c'">
          {{ __('If you are busy, number') }} {{ values[modalData.modalPhone] }} {{ __('Will receive the call') }}
        </p>
        <p v-if="modalData.modalPhone === 'nc_in_call_fwd_permanent_num_c'">
          {{ __('If there is no reception/device is turned off,') }}<br/> {{ __('number') }} {{ values[modalData.modalPhone] }} {{ __('Will receive the call') }}
        </p>
        <div>
          <button type="button" class="btn w160" @click="closeModal">
            {{ __('Approval') }}
          </button>
        </div>
      </template>
    </Modal>

  <ModalError v-if="showErrorModal" @close="showErrorModal = false" />
</template>
